import * as L from 'leaflet'
import { Observable, ObservableArray, observe, Scope } from 'wdc-cube'
import {
    type CompanyRow,
    type CompanySortModel,
    type GeoLocationMode,
    type ProfessionalExistsFilterField,
    type ProfessionalExistsFilterMode,
    type ProfessionalExistsFilters,
    type ProfessionalRow,
    type ProfessionalSortModel,
    type SortDirection
} from './ta_types'

import { type MedicoBean, type VinculoBean } from './ta_service'
import { SearchBoxScope, type TextualSearchRequest } from 'src/components/searchbox'
import { ConfirmExportDataLimitScope } from 'src/main/Main.scopes'

export {
    SearchBoxScope,
    type CompanyRow,
    type GeoLocationMode,
    type ProfessionalExistsFilterField,
    type ProfessionalExistsFilterMode,
    type ProfessionalExistsFilters,
    type ProfessionalRow,
    type SortDirection,
    type TextualSearchRequest
}

@Observable
export class HeaderScope extends Scope {
    @observe() onlyActives = false
    @observe() onlySemVinculo = false

    readonly search = new SearchBoxScope()

    onOnlyActivesChange = Scope.ASYNC_ACTION_BOOLEAN
    onOnlySemVinculoChange = Scope.ASYNC_ACTION_BOOLEAN
}

@Observable
export class ScoreCardScope extends Scope {
    @observe() label = ''
    @observe() value = ''
    @observe() unit = ''
}

@Observable
export class ProfessionalCardScope extends Scope {
    @observe() expanded = false
    @observe() rows = [] as ProfessionalRow[]
    @observe() total = 0
    @observe() page = 0
    @observe() pageSize = 100
    @observe() downloadEnabled = false
    @observe() working = false
    @observe() existsFilters: ProfessionalExistsFilters = {}

    readonly sortBy: ProfessionalSortModel[] = []

    onExpandClick = Scope.ASYNC_ACTION
    onShrunkClick = Scope.ASYNC_ACTION
    onDownloadClick = Scope.ASYNC_ACTION
    onPageChange = Scope.ASYNC_ACTION_NUMBER
    onPageSizeChange = Scope.ASYNC_ACTION_NUMBER
    onRowClick = Scope.ASYNC_ACTION_ONE<ProfessionalRow>()
    onDetailClick = Scope.ASYNC_ACTION_ONE<ProfessionalRow>()
    onSortModelChange = Scope.ASYNC_ACTION_ONE<ProfessionalSortModel[]>()
    onExistsFilterChange = Scope.ASYNC_ACTION_ONE<{
        field: ProfessionalExistsFilterField
        mode: ProfessionalExistsFilterMode
    }>()
    onClearExistsFilters = Scope.ASYNC_ACTION
}

@Observable
export class CompanyCardScope extends Scope {
    @observe() expanded = false
    @observe() rows = [] as CompanyRow[]
    @observe() total = 0
    @observe() page = 0
    @observe() pageSize = 100
    @observe() downloadEnabled = false
    @observe() working = false

    readonly sortBy: CompanySortModel[] = []

    onExpandClick = Scope.ASYNC_ACTION
    onShrunkClick = Scope.ASYNC_ACTION
    onDownloadClick = Scope.ASYNC_ACTION
    onPageChange = Scope.ASYNC_ACTION_NUMBER
    onPageSizeChange = Scope.ASYNC_ACTION_NUMBER
    onRowClick = Scope.ASYNC_ACTION_ONE<CompanyRow>()
    onSortModelChange = Scope.ASYNC_ACTION_ONE<CompanySortModel[]>()
}

@Observable
export class SliderScope extends Scope {
    @observe() min = 10
    @observe() max = 1000
    @observe() step = 10
    @observe() value = 50
    @observe() unit: 'km' | 'm' = 'm'
    @observe() visible = false

    onValueChanged = Scope.ASYNC_ACTION_NUMBER
}

@Observable
export class MapCardScope extends Scope {
    @observe() expanded = false
    @observe() showClearButton = false
    @observe() mode: GeoLocationMode = 'route'

    readonly slider = new SliderScope()

    onExpandClick = Scope.ASYNC_ACTION
    onShrunkClick = Scope.ASYNC_ACTION
    onMapChanged = Scope.ASYNC_ACTION_ONE<L.Map | null>()
    onClearFilters = Scope.ASYNC_ACTION
    onSetAreaMode = Scope.ASYNC_ACTION
    onSetRouteMode = Scope.ASYNC_ACTION
    onSetCircleMode = Scope.ASYNC_ACTION
}

@Observable
export class FilterEntryScope extends Scope {
    id = ''
    @observe() label = ''
    value: unknown

    onRemoveClick = Scope.ASYNC_ACTION
}

@Observable
export class OverlayLivreScope extends Scope {
    texts = new ObservableArray<FilterEntryScope>(this)

    getAllFilters() {
        return [this.texts]
    }

    hasContent() {
        return hasFilters(...this.getAllFilters())
    }
}

@Observable
export class OverlayEmpresaScope extends Scope {
    cnpjs = new ObservableArray<FilterEntryScope>(this)
    empresas = new ObservableArray<FilterEntryScope>(this)
    cnaes = new ObservableArray<FilterEntryScope>(this)
    empNatureza = new ObservableArray<FilterEntryScope>(this)
    empEnderecos = new ObservableArray<FilterEntryScope>(this)
    empCeps = new ObservableArray<FilterEntryScope>(this)
    empLogradouros = new ObservableArray<FilterEntryScope>(this)
    empBairros = new ObservableArray<FilterEntryScope>(this)
    empMunicipios = new ObservableArray<FilterEntryScope>(this)
    empUfs = new ObservableArray<FilterEntryScope>(this)

    getAllFilters() {
        return [
            this.cnpjs,
            this.empresas,
            this.cnaes,
            this.empNatureza,
            this.empEnderecos,
            this.empCeps,
            this.empLogradouros,
            this.empBairros,
            this.empMunicipios,
            this.empUfs
        ]
    }

    hasContent() {
        return hasFilters(...this.getAllFilters())
    }
}

@Observable
export class OverlayPessoaScope extends Scope {
    pessoas = new ObservableArray<FilterEntryScope>(this)
    pesEnderecos = new ObservableArray<FilterEntryScope>(this)
    pesCeps = new ObservableArray<FilterEntryScope>(this)
    pesLogradouros = new ObservableArray<FilterEntryScope>(this)
    pesBairros = new ObservableArray<FilterEntryScope>(this)
    pesMunicipios = new ObservableArray<FilterEntryScope>(this)
    pesCbos = new ObservableArray<FilterEntryScope>(this)

    getAllFilters() {
        return [
            this.pessoas,
            this.pesEnderecos,
            this.pesCeps,
            this.pesLogradouros,
            this.pesBairros,
            this.pesMunicipios,
            this.pesCbos
        ]
    }

    hasContent() {
        return hasFilters(...this.getAllFilters())
    }
}

@Observable
export class OverlaySocioScope extends Scope {
    socios = new ObservableArray<FilterEntryScope>(this)

    getAllFilters() {
        return [this.socios]
    }

    readonly hasContent = () => hasFilters(...this.getAllFilters())
}

@Observable
export class OverlayCnesScope extends Scope {
    cnes = new ObservableArray<FilterEntryScope>(this)
    cnesTipo = new ObservableArray<FilterEntryScope>(this)
    cnesEnderecos = new ObservableArray<FilterEntryScope>(this)
    cnesCeps = new ObservableArray<FilterEntryScope>(this)
    cnesLogradouros = new ObservableArray<FilterEntryScope>(this)
    cnesBairros = new ObservableArray<FilterEntryScope>(this)
    cnesMunicipios = new ObservableArray<FilterEntryScope>(this)
    cnesUfs = new ObservableArray<FilterEntryScope>(this)
    esaComplexidade = new ObservableArray<FilterEntryScope>(this)
    esaLeitosPorte = new ObservableArray<FilterEntryScope>(this)
    esaLeitosQtd = new ObservableArray<FilterEntryScope>(this)

    getAllFilters() {
        return [
            this.cnes,
            this.cnesTipo,
            this.cnesEnderecos,
            this.cnesCeps,
            this.cnesLogradouros,
            this.cnesBairros,
            this.cnesMunicipios,
            this.cnesUfs,
            this.esaComplexidade,
            this.esaLeitosPorte,
            this.esaLeitosQtd
        ]
    }

    hasContent() {
        return hasFilters(...this.getAllFilters())
    }
}

@Observable
export class OverlayPessoaAndSocioScope extends Scope {
    cpfs = new ObservableArray<FilterEntryScope>(this)
    cids = new ObservableArray<FilterEntryScope>(this)
    atos = new ObservableArray<FilterEntryScope>(this)
    atoCbos = new ObservableArray<FilterEntryScope>(this)
    espMeds = new ObservableArray<FilterEntryScope>(this)
    crms = new ObservableArray<FilterEntryScope>(this)
    crmUfs = new ObservableArray<FilterEntryScope>(this)
    crmDataInscricao = new ObservableArray<FilterEntryScope>(this)

    getAllFilters() {
        return [
            this.cpfs,
            this.cids,
            this.atos,
            this.atoCbos,
            this.espMeds,
            this.crms,
            this.crmUfs,
            this.crmDataInscricao
        ]
    }

    hasContent() {
        return hasFilters(...this.getAllFilters())
    }
}

@Observable
export class OverlayOperadoraScope extends Scope {
    empOps = new ObservableArray<FilterEntryScope>(this)
    prfOps = new ObservableArray<FilterEntryScope>(this)

    getAllFilters() {
        return [this.empOps, this.prfOps]
    }

    hasContent() {
        return hasFilters(...this.getAllFilters())
    }
}

function hasFilters(...filters: ObservableArray<FilterEntryScope>[]) {
    for (const filter of filters) {
        if (filter.length > 0) {
            return true
        }
    }
    return false
}

@Observable
export class OverlayScope extends Scope {
    @observe() expanded = false
    @observe() downloading = false
    @observe() downloadEnabled = false

    livre = new OverlayLivreScope()
    empresa = new OverlayEmpresaScope()
    pessoa = new OverlayPessoaScope()
    cnes = new OverlayCnesScope()
    socios = new OverlaySocioScope()
    pessoaAndSocio = new OverlayPessoaAndSocioScope()
    operadoras = new OverlayOperadoraScope()

    onFilterToggleClick = Scope.ASYNC_ACTION
    onDownloadClick = Scope.ASYNC_ACTION
}

@Observable
export class ProfessionalDetailScope extends Scope {
    @observe() open = false
    @observe() loading = false
    @observe() error = ''
    @observe() profissional?: MedicoBean
    @observe() vinculos = [] as VinculoBean[]
    @observe() semVinculo = false

    onClose = Scope.ASYNC_ACTION
    onFilterByProfessional = Scope.ASYNC_ACTION
}

@Observable
export class TheActingScope extends Scope {
    exportDataLimitScope = new ConfirmExportDataLimitScope()
    readonly professionalDetail = new ProfessionalDetailScope()

    @observe() selectedCard: 'none' | 'professional' | 'company' | 'map' = 'none'
    @observe() downloadMode = 'all' as 'company' | 'professional' | 'all'

    readonly overlay = new OverlayScope()

    readonly profissionalScoreCard = new ScoreCardScope()
    readonly companyScoreCard = new ScoreCardScope()
    readonly revenueScoreCard = new ScoreCardScope()
    readonly healthCoScoreCard = new ScoreCardScope()

    readonly professionalCard = new ProfessionalCardScope()
    readonly companyCard = new CompanyCardScope()
    readonly mapCard = new MapCardScope()

    readonly keyboard = {
        ctrlKey: false
    }
}

let DEFAULT_CENTER: L.LatLngLiteral = { lat: -15.7754462, lng: -47.7970891 }

export const ScopeDefaults = new (class {
    getDefaultCenter(): L.LatLngLiteral {
        return { ...DEFAULT_CENTER }
    }

    setDefaultCenter(value: L.LatLngLiteral) {
        DEFAULT_CENTER = value
    }
})()
